import {ConfigProvider} from 'antd'
import {
    doc_getCodiDocumentsRowsByPage_Api,
    docs_getCodiDocumentsRowDetails_Api,
    docs_getCodiExtraDataById_Api,
    docs_getSubstantiveById_Api,
} from 'apis'
import EmptyHolder from 'components/EmptyHolder'
import {UseHook} from 'components/UseHook'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import PermissionProvider from 'modules/permissions/PermissionProvider'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Switch} from 'react-router-dom'
import RouteWithSubRoutes from 'routes/RouteWithSubRoutes'
import {getResponseItem, NullAsDefault} from 'views/Shared'
import {CodiActions} from './codiHelpers'
import {notifyOnError} from '../factory/createErrorEvent'
import {useCodiBookmark} from './CodiBookmark'
import {getActionRoute, routes} from './routes'
import {useSelectEntities} from "../../../redux/entities/useSelectEntities";
import {codiDocumentSchema} from "../../../apis/schema";
import {getRouteSearchParams, useRouteSearchParam} from "../../../envs/_current/history";
import {decryptContent} from "../../../helpers/dataHelpers";

const CodiContext = React.createContext({})

const Provider = ({
                      id,
                      children,
                      initialCurrent,
                      useExtra = true,
                      filterActions = {
                          page: 'row',
                          variant_version: 'variant-version',
                          variant_page: 'variant-row',
                          s_compare: 's-compare',
                      },
                      ...props
                  }) => {
    const t = useTranslate()
    const history = useHistory()
    const asyncRowRef = useRef(null)
    const modalDismissRef = useRef(NullAsDefault)
    const row_query = useRouteSearchParam(filterActions?.page) || props?.page_id
    const variant_version = useRouteSearchParam(filterActions?.variant_version)
    const variant_row_query = useRouteSearchParam(filterActions?.variant_page)
    const s_compare = useRouteSearchParam(filterActions?.s_compare)
    const detail_id = useRouteSearchParam("detail_id")
    const [refreshKey, setRefreshKey] = useState()
    const [current_row, set_current_row] = useState()
    const [current_variant_row, set_current_variant_row] = useState()
    const [system_row, set_system_row] = useState()
    const [showCompare, setShowCompare] = useState(!!Number(s_compare))
    const [variantVersion, setVariantVersion] = useState(variant_version)


    const [selected_row_id, set_selected_row_id] = useState(row_query)

    const [current, setCurrent] = useState(initialCurrent)

    const cacheCodiRow = (codi_row) => {
        const row_id = codi_row?.id
        if (row_id) {
            // let newCache = Object.assign({}, rows_cache)
            // newCache[row_id] = codi_row
            // set_rows_cache(newCache)
            set_current_row(codi_row);

            set_current_variant_row({
                ...codi_row?.variant,
            })
            if (_.isEmpty(detail_id)) {
                set_system_row(codi_row)
            }
        }
    }

    const {
        response,
        isLoading = false,
        handleAsyncAction: getDocument = NullAsDefault,
    } = useAsyncAction({
        apiInfo: docs_getSubstantiveById_Api,
    })

    // Extra data hook — gọi imperatively sau khi isSucceed=true.
    // Khai báo ở đây để hook order ổn định, nhưng KHÔNG dùng response/isLoading
    // vì extra data là fire-and-forget, không cần drive UI.
    const {handleAsyncAction: getCodiExtraDataById = NullAsDefault} = useAsyncAction({
        apiInfo: docs_getCodiExtraDataById_Api,
    })

    useEffect(() => {
        setVariantVersion(variant_version)
    }, [variant_version])

    useEffect(() => {
        setShowCompare(!!Number(s_compare))
    }, [s_compare])

    // const withFirstRow = row_query ? "n" : "y"
    useEffect(() => {
        if (!!!variantVersion) {
            getDocument({}, {id})
        } else {
            getDocument({variant_codi_document_id: variantVersion}, {id})
        }
    }, [id, variantVersion])

    const asyncRow = useAsyncAction({
        apiInfo: doc_getCodiDocumentsRowsByPage_Api,
        onError: (e, o) => {
            notifyOnError(t)(e, o)
            // set_rows_cache({})
            set_current_row(undefined)
            set_current_variant_row(undefined)
            set_system_row(undefined)
        },
        onSuccess: (__, data) => {
            const codi_row = getResponseItem(data?.response)
            cacheCodiRow(codi_row)
        },
    })

    useEffect(() => {
        asyncRowRef.current = asyncRow
    })


    const item = useSelectEntities(id, codiDocumentSchema);
    const error = response?.error
    const request_status = response?.status
    const errorMessage = response?.errorMessages?.error
    const error_code = response?.errorMessages?.error_code
    const status = {
        title: request_status,
        description: errorMessage,
    }
    const isSucceed = Number(request_status) === 200

    // Extra data: gọi sau 3s khi document đã load thành công.
    // - useEffect đặt SAU khi isSucceed được khai báo để tránh Temporal Dead Zone
    // - cleanup timer + idleCallback nếu unmount trước khi kịp gọi
    // - requestIdleCallback nhường browser paint/layout trước, mới gọi API
    useEffect(() => {
        if (!isSucceed || !useExtra || !id) return
        let idleId = null
        const timerId = setTimeout(() => {
            const doFetch = () => getCodiExtraDataById({}, {id})
            if (typeof requestIdleCallback === 'function') {
                idleId = requestIdleCallback(doFetch, {timeout: 5000})
            } else {
                doFetch()
            }
        }, 3000)
        return () => {
            clearTimeout(timerId)
            if (idleId !== null && typeof cancelIdleCallback === 'function') {
                cancelIdleCallback(idleId)
            }
        }
    }, [isSucceed, id, useExtra])

    const decrypted_item = useMemo(() => {
        const indexes = item?.table_of_contents ?? item?.rows
        return {
            ...(item ?? {}),
            rows: indexes ? decryptContent(indexes, item) : [],
        }
    }, [item?.id])

    const rows = Array.from(decrypted_item?.rows ?? [])

    const root = item?.codi_document_row_first_page
    const variant_root = item?.variant?.codi_document_row_first_page

    // ── Effect B: fetch row khi row_query hoặc variantVersion thay đổi ──
    // BUG FIX: thêm variantVersion vào deps để re-fetch variant row khi bật so sánh
    // mà row_query không đổi (user đang ở chương đã chọn rồi mới bật compare).
    // Dùng trực tiếp state variantVersion thay vì ref để effect phản ứng đúng.
    useEffect(() => {
        if (!row_query) return
        const {handleAsyncAction = NullAsDefault} = asyncRowRef.current ?? {}
        handleAsyncAction(
            {
                variant_codi_document_row_id: variant_row_query,
                ...(variantVersion ? {variant_codi_document_id: variantVersion} : {})
            },
            {id: row_query}
        )
    }, [row_query, variant_row_query, variantVersion])

// ── Effect C: fallback root — CHỈ khi không có row_query ──
    useEffect(() => {
        if (!root?.id || row_query) return;

        set_current_row(root)
        set_current_variant_row(variant_root)
        set_system_row(root)
    }, [root?.id, variant_root?.id])

    const onRefresh = () => {
        // set_rows_cache({})
        setRefreshKey(Date.now())
    }

    const notifyRowChange = (row_id) => {

    }

    const bookmarkManager = useCodiBookmark(id, {
        onSuccess: (row) => {
            const row_id = row?.id
            if (row_id) {
                notifyRowChange()
                set_current_row({
                    ...(current_row ?? {}),
                    bookmark_status: Boolean(row?.bookmark_status),
                })
            }
        },
    })

    const asyncRowDetail = useAsyncAction({
        onError: notifyOnError(t),
        apiInfo: docs_getCodiDocumentsRowDetails_Api,
        onSuccess: (__, data) => {
            const row = getResponseItem(data?.response)
            handleAction(CodiActions.SET_SYSTEM_ROW, row)
        },
    })

    const handleAction = (action, params) => {
        switch (action) {
            case CodiActions.SELECT_SYSTEM_ROW:
                const {handleAsyncAction = NullAsDefault} = asyncRowDetail ?? {}
                handleAsyncAction({}, params)
                break
            case CodiActions.SET_SYSTEM_ROW:
                set_system_row(params)
                break
            case CodiActions.SELECT_ROW:
                const {id: page_id, detail_id, ref_detail_id, variant_codi_document_row_id} = params ?? {}
                const searchParams = getRouteSearchParams()
                searchParams.set(filterActions.page, page_id)

                if (!!variant_codi_document_row_id) {
                    searchParams.set(filterActions.variant_page, variant_codi_document_row_id)
                } else {
                    if (Number(searchParams.get("s-compare")) === 1) {
                        searchParams.delete(filterActions.variant_page);
                    }
                }

                if (detail_id && detail_id !== page_id) {
                    searchParams.set('detail_id', detail_id)
                    if (ref_detail_id) {
                        searchParams.set('ref_detail_id', ref_detail_id)
                    }
                } else {
                    searchParams.delete('detail_id')
                    searchParams.delete('ref_detail_id')
                }

                history.push({
                    search: '?' + searchParams.toString()
                })
                set_selected_row_id(page_id)
                const markAsRecent = bookmarkManager?.setLasted ?? NullAsDefault
                if (_.isFunction(markAsRecent)) {
                    setTimeout(() => {
                        markAsRecent(page_id)
                    }, 500)
                }
                break
            case CodiActions.SHOW_INFO_MODAL:
            case CodiActions.SHOW_NOTES_MODAL:
            case CodiActions.SHOW_LINKS_MODAL:
            case CodiActions.SHOW_ADMIN_MODAL:
            case CodiActions.SHOW_DETAIL_MODAL:
            case CodiActions.SHOW_SEARCH_MODAL:
            case CodiActions.SHOW_INDEXES_MODAL:
            case CodiActions.SHOW_KEYWORDS_MODAL:
            case CodiActions.SHOW_EDIT_CONTENT_MODAL:
            case CodiActions.SHOW_RELATED_CODI_MODAL:
            case CodiActions.SHOW_BROS_CODI_MODAL:
            case CodiActions.SHOW_INSERT_CONTENT_MODAL:
            case CodiActions.SHOW_UPDATE_ROW_TYPE_MODAL:
            case CodiActions.SHOW_RELATED_ARTICLES_MODAL:
            case CodiActions.SHOW_REARRANGE_CONTENT_MODAL:
                const pathname = getActionRoute(action)?.path
                if (pathname) {
                    setCurrent(pathname)
                }
                break
            default:
                break
        }
    }

    return (
        <CodiContext.Provider
            key={id}
            value={{
                ...props,
                id,
                rows,
                root,
                error,
                status,
                current,
                asyncRow,
                isDocLoading: isLoading,
                isRowLoading: asyncRow?.isLoading,
                isLoading,
                isSucceed,
                onRefresh,
                setCurrent,
                refreshKey,
                error_code,
                errorMessage,
                current_row,
                current_variant_row,
                handleAction,
                filterActions,
                modalDismissRef,
                selected_row_id,
                bookmarkManager,
                notifyRowChange,
                asyncRowDetail,
                item: decrypted_item,
                showCompare, setShowCompare, variantVersion, setVariantVersion,
                set_system_row,
                system_row,
            }}>
            <PermissionProvider
                permissions={{
                    edit: !!decrypted_item?.edit,
                    ...(decrypted_item?.available_actions ?? {}),
                }}>
                <React.Fragment>
                    {children}
                    {current && (
                        <Switch
                            location={{
                                hash: '',
                                search: '',
                                key: current,
                                pathname: current,
                            }}>
                            {Object.values(routes).map((route, index) => (
                                <RouteWithSubRoutes
                                    key={index}
                                    {...route}
                                />
                            ))}
                        </Switch>
                    )}
                </React.Fragment>
            </PermissionProvider>
        </CodiContext.Provider>
    )
}

export const CodiProvider = (props) => (
    <UseHook
        hook={useRouteSearchParam}
        deps={['initialCurrent']}>
        {(initialCurrent) => (
            <ConfigProvider renderEmpty={EmptyHolder}>
                <Provider
                    {...props}
                    initialCurrent={initialCurrent}
                />
            </ConfigProvider>
        )}
    </UseHook>
)

export default CodiContext